import { useMemo, useState } from "react";

import { useTheme } from "~/contexts/ThemeContext";

// Shared chart palette for Google Dashboard widgets. Mirrors the
// Visibility Engine breakdown palette so the two pages look consistent.
const PALETTE_VARS = [
  "--functional-primary",
  "--service-violete2",
  "--service-yellow3",
  "--service-green3",
  "--service-red3",
] as const;

export type ChartTheme = {
  /** Series color palette resolved from CSS variables. */
  palette: string[];
  /** Axis label / muted text color. */
  textGrey: string;
  /** Default text color for headings inside charts (donut centre, etc.). */
  textBlack: string;
  /** Grid line color (matches VE charts). */
  gridBorder: string;
  /** Card background — used as the "gap" color between donut slices so
   *  they appear visually separated against the card. Theme-aware. */
  bgCard: string;
  /** Apex font family for all chart text. */
  fontFamily: string;
  /** Current Mantine theme — used to drive Apex tooltip theme. */
  theme: "light" | "dark";
};

function readChartVars(): Omit<ChartTheme, "theme"> {
  const s = getComputedStyle(document.documentElement);
  const v = (name: string) => s.getPropertyValue(name).trim();

  return {
    palette: PALETTE_VARS.map(v),
    textGrey: v("--text-grey"),
    textBlack: v("--text-black"),
    gridBorder: v("--functional-grey2"),
    bgCard: v("--bg-white"),
    fontFamily: "Lato, sans-serif",
  };
}

/**
 * Resolve theme-aware chart styling at runtime. CSS variables can't be
 * passed directly to ApexCharts (it bakes them into SVG attributes), so
 * we read them via getComputedStyle and re-read whenever the user
 * toggles light/dark mode.
 *
 * The returned object is referentially stable across renders (only a
 * fresh reference when `theme` actually changes), so consumers can use
 * it as a `useMemo` dep without invalidating their cache on every
 * parent re-render.
 *
 * Uses React's "derive state from prop" pattern (compare-and-set during
 * render) instead of useEffect — we deliberately avoid scheduling a
 * post-mount setVars() that would invalidate downstream useMemo caches
 * within the initial chart animation window. Without this, Apex sees
 * fresh options/series after first paint and calls updateOptions(),
 * which short-circuits the entrance animation.
 */
export function useChartTheme(): ChartTheme {
  const { theme } = useTheme();
  const [storedTheme, setStoredTheme] = useState(theme);
  const [vars, setVars] = useState(readChartVars);

  if (storedTheme !== theme) {
    setStoredTheme(theme);
    setVars(readChartVars());
  }

  return useMemo(() => ({ ...vars, theme }), [vars, theme]);
}
